import { useEffect, useRef, useState } from "react";
import { Camera, X, ArrowsClockwise, Check, UploadSimple, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloatField } from "@/components/PageHeader";

/**
 * Business Card scanner: capture via camera OR upload → OCR → review → save/merge.
 * Props: onSaved(contact), onClose()
 */
export default function BusinessCardScanner({ onSaved, onClose }) {
  const [mode, setMode] = useState("choose"); // choose | camera | preview | review
  const [imageBase64, setImageBase64] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [primaryDupe, setPrimaryDupe] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => () => stopCamera(), []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setMode("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      toast.error("Cannot access camera — try uploading instead");
      setMode("choose");
    }
  };

  const capture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvasRef.current = canvas;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setImageBase64(dataUrl);
    stopCamera();
    setMode("preview");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|jpg)/.test(f.type)) {
      toast.error("Please choose a JPEG, PNG or WEBP image");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setImageBase64(reader.result); setMode("preview"); };
    reader.readAsDataURL(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile({ target: { files: [f] } });
  };

  const extract = async () => {
    if (!imageBase64) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ocr/business-card", { image_base64: imageBase64, mime: "image/jpeg" });
      setExtracted(data.extracted);
      setDuplicates(data.duplicates || []);
      if (data.duplicates?.length) setPrimaryDupe(data.duplicates[0]);
      setMode("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI extraction failed");
    }
    setBusy(false);
  };

  const save = async () => {
    if (!extracted) return;
    setBusy(true);
    try {
      let contact;
      if (primaryDupe) {
        // Merge: primary keeps most of its fields, new fields from extracted fill blanks
        const resolved = {};
        Object.keys(extracted).forEach((k) => {
          if (["confidence", "raw_text", "social"].includes(k)) return;
          const val = extracted[k];
          if (val && !primaryDupe[k]) resolved[k] = val;
        });
        const { data } = await api.post("/ocr/contacts/merge", {
          primary_id: primaryDupe._id, merge_ids: [], resolved,
        });
        contact = data.primary;
        toast.success("Contact updated (merged from scan)");
      } else {
        const payload = { ...extracted, contact_type: extracted.contact_type || "other" };
        delete payload.confidence; delete payload.raw_text; delete payload.social;
        const { data } = await api.post("/contacts", payload);
        contact = data;
        toast.success("Contact created from business card");
      }
      if (onSaved) onSaved(contact);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={() => { stopCamera(); onClose && onClose(); }} data-testid="bcard-scanner">
      <div className="modal-surface modal-scale w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-7 pt-7 pb-4 border-b border-border/60 flex items-center justify-between sticky top-0 bg-popover z-10">
          <div>
            <div className="label-eyebrow">AI · Business card scanner</div>
            <div className="display-3 mt-1">Capture, extract & save</div>
          </div>
          <button onClick={() => { stopCamera(); onClose && onClose(); }} className="btn-ghost btn-icon" data-testid="bcard-close"><X size={16}/></button>
        </div>

        <div className="p-7">
          <div className="step-progress mb-6">
            <div className={`step ${mode === "choose" ? "is-active" : mode === "preview" || mode === "camera" || mode === "review" ? "is-done" : ""}`}><span className="dot">1</span><span className="text-xs">Capture</span></div>
            <div className="bar"/>
            <div className={`step ${mode === "preview" ? "is-active" : mode === "review" ? "is-done" : ""}`}><span className="dot">2</span><span className="text-xs">Extract</span></div>
            <div className="bar"/>
            <div className={`step ${mode === "review" ? "is-active" : ""}`}><span className="dot">3</span><span className="text-xs">Review & save</span></div>
          </div>

          {mode === "choose" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={startCamera} className="upload-zone flex flex-col items-center gap-3 py-10" data-testid="bcard-start-camera">
                <Camera size={30} weight="duotone" className="text-[color:hsl(var(--tint-terracotta))]"/>
                <div className="font-display font-semibold">Use camera</div>
                <div className="text-xs text-muted-foreground">Live-scan a physical card</div>
              </button>
              <label onDrop={onDrop} onDragOver={(e)=>e.preventDefault()} className="upload-zone flex flex-col items-center gap-3 py-10 cursor-pointer" data-testid="bcard-start-upload">
                <UploadSimple size={30} weight="duotone" className="text-[color:hsl(var(--tint-indigo))]"/>
                <div className="font-display font-semibold">Upload image</div>
                <div className="text-xs text-muted-foreground">Drag & drop or click · JPG / PNG / WEBP</div>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="bcard-file-input"/>
              </label>
            </div>
          )}

          {mode === "camera" && (
            <div>
              <div className="camera-frame">
                <video ref={videoRef} playsInline muted/>
                <div className="scan-guide"/>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => { stopCamera(); setMode("choose"); }} className="btn-ghost flex-1">Back</button>
                <button onClick={capture} className="btn-primary flex-1" data-testid="bcard-capture"><Camera size={14}/> Capture</button>
              </div>
            </div>
          )}

          {mode === "preview" && (
            <div>
              <div className="upload-preview mb-5">
                <img src={imageBase64} alt="business card preview"/>
                <button className="remove-badge" onClick={() => { setImageBase64(null); setMode("choose"); }} data-testid="bcard-remove"><X size={12}/></button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setMode("choose")} className="btn-ghost flex-1">Retake</button>
                <button onClick={extract} disabled={busy} className="btn-primary flex-1" data-testid="bcard-extract">
                  {busy ? <><ArrowsClockwise size={14} className="animate-spin"/> Extracting…</> : <><Check size={14}/> Extract with AI</>}
                </button>
              </div>
            </div>
          )}

          {mode === "review" && extracted && (
            <div>
              {duplicates.length > 0 && (
                <div className="section-card mb-5" style={{background: "hsl(var(--warning-soft) / 0.4)"}}>
                  <div className="flex items-start gap-3">
                    <Warning size={18} className="text-[color:hsl(var(--warning))] mt-1"/>
                    <div className="flex-1">
                      <div className="section-title">Possible duplicate detected</div>
                      <p className="text-xs text-muted-foreground mt-1">Merge into an existing contact instead of creating a new one?</p>
                      <div className="mt-4 space-y-2">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="radio" name="dupe" checked={!primaryDupe} onChange={() => setPrimaryDupe(null)} className="mt-1"/>
                          <div className="text-sm"><span className="font-medium">Create new contact</span><div className="text-xs text-muted-foreground">Ignore existing matches</div></div>
                        </label>
                        {duplicates.map((d) => (
                          <label key={d._id} className="flex items-start gap-3 cursor-pointer" data-testid={`bcard-dupe-${d._id}`}>
                            <input type="radio" name="dupe" checked={primaryDupe?._id === d._id} onChange={() => setPrimaryDupe(d)} className="mt-1"/>
                            <div className="text-sm">
                              <span className="font-medium">Merge with {d.name}</span>
                              <div className="text-xs text-muted-foreground">{d.company || "—"} · {d.phone || "—"} · {d.email || "—"} · <span className="font-mono">{d.match_score}% match</span></div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {[["name","Full name"],["designation","Designation"],["company","Company"],["phone","Phone"],["email","Email"],["website","Website"],["address","Address"],["city","City"],["state","State"],["gstin","GSTIN"],["pan","PAN"]].map(([k,l])=>(
                  <FloatField key={k} label={l} value={extracted[k] || ""} onChange={(e)=>setExtracted({...extracted, [k]: e.target.value})} testid={`bcard-field-${k}`} className={k === "address" ? "col-span-2" : ""}/>
                ))}
                <FloatField label="Contact type" className="col-span-2 is-select">
                  <select value={extracted.contact_type || "other"} onChange={(e)=>setExtracted({...extracted, contact_type:e.target.value})} data-testid="bcard-field-type">
                    {["supplier","manufacturer","jobworker","designer","photographer","model","customer","wholesale_buyer","legal_consultant","other"].map(t=><option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
                  </select>
                </FloatField>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">AI confidence: <span className="tabular font-medium">{Math.round((extracted.confidence || 0) * 100)}%</span> · Please review before saving.</div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setMode("preview")} className="btn-ghost flex-1">Back</button>
                <button onClick={save} disabled={busy} className="btn-primary flex-1" data-testid="bcard-save">
                  {busy ? <><ArrowsClockwise size={14} className="animate-spin"/> Saving…</> : primaryDupe ? "Merge & save" : "Create contact"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
